'use client';

import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useState } from 'react';
import { motion } from 'framer-motion';
import {
  ChevronDown,
  ChevronUp,
  MapPin,
  BedDouble,
  Sofa,
  Building2,
  ArrowRight,
  User,
  Mail,
  Phone,
  Home,
  CheckCircle2,
} from "lucide-react";
import Image from 'next/image';
import React, { useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectItem, SelectContent, SelectTrigger, SelectValue } from '@/components/ui/select';
import { hostEnquiryAPI } from "@/lib/api";
import { toast } from "sonner";

export const bedOptions = ["Studio", "1", "2", "3", "4", "5", "6", "7", "7+"];
export const bathOptions = ["1", "2", "3", "4", "5", "6", "7", "7+"];

interface LandlordHeroProps {
  currentStep: number;
  setCurrentStep: (step: number) => void;
  isFilterOpen: boolean;
  setIsFilterOpen: (open: boolean) => void;
  formData: {
    name: string;
    mobile: string;
    email: string;
    propertyName: string;
    furnishStatus: string;
    selectedBeds: string[];
    selectedBaths: string[];
    propertyType: string;
    area: string;
  };
  setFormData: (data: any) => void;
  areaList: any[];
  handleSearch: () => void;
}

const LandlordHero: React.FC<LandlordHeroProps> = ({
  currentStep,
  setCurrentStep,
  isFilterOpen,
  setIsFilterOpen,
  formData,
  setFormData,
  areaList,
  handleSearch,
}) => {
  const [loading, setLoading] = useState(false);
  const searchFilterRef = useRef<HTMLDivElement>(null);

  const handleRestartFlow = () => {
    setCurrentStep(1);
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      toast.error("Please enter your name");
      return;
    }

    if (!formData.mobile.trim()) {
      toast.error("Please enter your mobile number");
      return;
    }

    if (!formData.email.trim()) {
      toast.error("Please enter your email");
      return;
    }

    try {
      setLoading(true);

      const payload = {
        name: formData.name,
        mobile: formData.mobile,
        email: formData.email,
        propertyName: formData.propertyName,

        area: formData.area,
        furnishStatus: formData.furnishStatus,
        selectedBeds: formData.selectedBeds,
        selectedBaths: formData.selectedBaths,
        propertyType: formData.propertyType,
      };

      console.log(payload);

      const res = await hostEnquiryAPI.createWeb(payload);

      if (res.success) {
        setCurrentStep(3);
      } else {
        toast.error(res.message);
      }
    } catch (error) {
      console.error(error);
      toast.error("Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  const getDisplayText = () => {
    if (formData.selectedBeds.length === 0 && formData.selectedBaths.length === 0) {
      return "Select Beds & Baths";
    }

    const bedsText = formData.selectedBeds.length === 0 ? "Any Beds" :
      formData.selectedBeds.length === 1 ? `${formData.selectedBeds[0]} Bed` : `${formData.selectedBeds.length} Beds`;

    const bathsText = formData.selectedBaths.length === 0 ? "Any Baths" :
      formData.selectedBaths.length === 1 ? `${formData.selectedBaths[0]} Bath` : `${formData.selectedBaths.length} Baths`;

    return `${bedsText}; ${bathsText}`;
  };

  const handleBedToggle = (option: string) => {
    handlePropertyDataChange('selectedBeds',
      formData.selectedBeds.includes(option)
        ? formData.selectedBeds.filter(bed => bed !== option)
        : [...formData.selectedBeds, option]
    );
  };

  const handleBathToggle = (option: string) => {
    handlePropertyDataChange('selectedBaths',
      formData.selectedBaths.includes(option)
        ? formData.selectedBaths.filter(bath => bath !== option)
        : [...formData.selectedBaths, option]
    );
  };

  const handleReset = () => {
    handlePropertyDataChange('selectedBeds', []);
    handlePropertyDataChange('selectedBaths', []);
  };

  const handleFormChange = (field: string, value: string) => {
    setFormData((prev: any) => ({ ...prev, [field]: value }));
  };

  const handlePropertyDataChange = (field: string, value: any) => {
    setFormData((prev: any) => ({ ...prev, [field]: value }));
  };

  return (
    <section
      id="home"
      className="relative overflow-hidden min-h-[95vh] flex items-center"
    >
      <div className="absolute inset-0">
        {/* Background Video */}
        <video
          autoPlay
          muted
          loop
          playsInline
          poster="https://images.pexels.com/photos/3320508/pexels-photo-3320508.jpeg"
          className="absolute inset-0 w-full h-full object-cover scale-105"
        >
          <source src="https://18bricks.s3.ap-southeast-2.amazonaws.com/Web_holiday_1.mp4" type="video/mp4" />

          {/* Fallback Image if video is not supported */}
          <Image
            src="https://images.pexels.com/photos/3320508/pexels-photo-3320508.jpeg"
            alt="Luxury Villa"
            fill
            priority
            className="object-cover scale-105"
          />
        </video>

        {/* Overlay */}
        <div className="absolute inset-0 " />
      </div>

      <div className="relative z-20 container mx-auto px-6 lg:px-10">
        {/* Search Filter */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{
            duration: .8
          }}
        >
          <div className="w-full max-w-7xl mx-auto px-4 rounded-xl" ref={searchFilterRef}>
            {/* Dark gray container */}
            <div
              className="bg-white/10 border border-white/20 shadow-2xl rounded-3xl p-6 lg:p-8">
              {/* White input field container */}
              {currentStep !== 3 && (
                <h2 className="text-2xl font-semibold text-white text-center mb-8">

                  Calculate Your Rental Potential

                </h2>
              )}
              <div
                className="
bg-white
rounded-2xl
shadow-xl
p-4
lg:p-5
">
                {/* Step 1 */}
                {currentStep === 1 && (
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-5 w-full">

                    {/* AREA */}

                    <div className="rounded-2xl border border-gray-200 hover:border-primary transition-all bg-gray-50 hover:bg-white p-4">

                      <div className="flex items-center gap-2 mb-2">

                        <MapPin className="w-5 h-5 text-primary" />

                        <span className="text-xs uppercase tracking-wide text-gray-500">

                          Area

                        </span>

                      </div>

                      <Select
                        value={formData.area}
                        onValueChange={(value) =>
                          handlePropertyDataChange("area", value)
                        }
                      >
                        <SelectTrigger className="border-0 shadow-none p-0 h-auto font-semibold">

                          <SelectValue placeholder="Select Area" />

                        </SelectTrigger>

                        <SelectContent>

                          {areaList?.map((area: any) => (

                            <SelectItem key={area._id} value={area._id}>

                              {area.name}

                            </SelectItem>

                          ))}

                        </SelectContent>

                      </Select>

                    </div>

                    {/* BEDS */}

                    <div className="rounded-2xl border border-gray-200 hover:border-primary transition-all bg-gray-50 hover:bg-white p-4">

                      <div className="flex items-center gap-2 mb-2">

                        <BedDouble className="w-5 h-5 text-primary" />

                        <span className="text-xs uppercase tracking-wide text-gray-500">

                          Bedrooms

                        </span>

                      </div>

                      <Popover open={isFilterOpen} onOpenChange={setIsFilterOpen}>

                        <PopoverTrigger asChild>

                          <Button
                            variant="ghost"
                            className="w-full justify-between p-0 h-auto font-semibold hover:bg-transparent"
                          >

                            {getDisplayText()}

                            {isFilterOpen ? (
                              <ChevronUp className="w-4 h-4" />
                            ) : (
                              <ChevronDown className="w-4 h-4" />
                            )}

                          </Button>

                        </PopoverTrigger>

                        <PopoverContent
                          className="w-[95vw] sm:w-[460px] rounded-2xl border border-gray-200 shadow-2xl p-6 bg-white"
                          align="start"
                        >
                          {/* Bedrooms */}
                          <div className="mb-6">
                            <h4 className="text-sm font-semibold text-gray-800 mb-3">
                              Bedrooms
                            </h4>

                            <div className="flex flex-wrap gap-2">
                              {bedOptions.map((option) => (
                                <button
                                  key={option}
                                  type="button"
                                  onClick={() => handleBedToggle(option)}
                                  className={`px-4 py-2 rounded-xl border text-sm font-medium transition-all duration-200 ${formData.selectedBeds.includes(option)
                                    ? "bg-primary text-white border-primary shadow-md"
                                    : "bg-white border-gray-300 text-gray-700 hover:border-primary hover:text-primary"
                                    }`}
                                >
                                  {option}
                                </button>
                              ))}
                            </div>
                          </div>

                          {/* Divider */}
                          <div className="border-t border-gray-200 my-6" />

                          {/* Bathrooms */}
                          <div className="mb-6">
                            <h4 className="text-sm font-semibold text-gray-800 mb-3">
                              Bathrooms
                            </h4>

                            <div className="flex flex-wrap gap-2">
                              {bathOptions.map((option) => (
                                <button
                                  key={option}
                                  type="button"
                                  onClick={() => handleBathToggle(option)}
                                  className={`px-4 py-2 rounded-xl border text-sm font-medium transition-all duration-200 ${formData.selectedBaths.includes(option)
                                    ? "bg-primary text-white border-primary shadow-md"
                                    : "bg-white border-gray-300 text-gray-700 hover:border-primary hover:text-primary"
                                    }`}
                                >
                                  {option}
                                </button>
                              ))}
                            </div>
                          </div>

                          {/* Footer Buttons */}
                          <div className="flex justify-between items-center pt-4 border-t border-gray-200">
                            <Button
                              type="button"
                              variant="outline"
                              className="rounded-xl"
                              onClick={handleReset}
                            >
                              Reset
                            </Button>

                            <Button
                              type="button"
                              className="rounded-xl"
                              onClick={() => setIsFilterOpen(false)}
                            >
                              Apply
                            </Button>
                          </div>
                        </PopoverContent>

                      </Popover>

                    </div>

                    {/* FURNISH */}

                    <div className="rounded-2xl border border-gray-200 hover:border-primary transition-all bg-gray-50 hover:bg-white p-4">

                      <div className="flex items-center gap-2 mb-2">

                        <Sofa className="w-5 h-5 text-primary" />

                        <span className="text-xs uppercase tracking-wide text-gray-500">

                          Furnish

                        </span>

                      </div>

                      <Select
                        value={formData.furnishStatus}
                        onValueChange={(value) =>
                          handlePropertyDataChange("furnishStatus", value)
                        }
                      >

                        <SelectTrigger className="border-0 shadow-none p-0 h-auto font-semibold">

                          <SelectValue placeholder="Select" />

                        </SelectTrigger>

                        <SelectContent>

                          <SelectItem value="Furnished">
                            Furnished
                          </SelectItem>

                          <SelectItem value="Semi-Furnished">
                            Semi Furnished
                          </SelectItem>

                          <SelectItem value="Unfurnished">
                            Unfurnished
                          </SelectItem>

                        </SelectContent>

                      </Select>

                    </div>

                    {/* PROPERTY */}

                    <div className="rounded-2xl border border-gray-200 hover:border-primary transition-all bg-gray-50 hover:bg-white p-4">

                      <div className="flex items-center gap-2 mb-2">

                        <Building2 className="w-5 h-5 text-primary" />

                        <span className="text-xs uppercase tracking-wide text-gray-500">

                          Property

                        </span>

                      </div>

                      <Select
                        value={formData.propertyType}
                        onValueChange={(value) =>
                          handlePropertyDataChange("propertyType", value)
                        }
                      >

                        <SelectTrigger className="border-0 shadow-none p-0 h-auto font-semibold">

                          <SelectValue placeholder="Select Type" />

                        </SelectTrigger>

                        <SelectContent>

                          <SelectItem value="Apartment">
                            Apartment
                          </SelectItem>

                          <SelectItem value="Villa">
                            Villa
                          </SelectItem>

                          <SelectItem value="Townhouse">
                            Townhouse
                          </SelectItem>

                          <SelectItem value="Studio">
                            Studio
                          </SelectItem>

                          <SelectItem value="Penthouse">
                            Penthouse
                          </SelectItem>

                        </SelectContent>

                      </Select>

                    </div>

                    {/* BUTTON */}

                    <Button
                      onClick={handleSearch}
                      className="
      h-full
      min-h-[88px]
      rounded-2xl
      text-lg
      font-semibold
      shadow-xl
      hover:scale-[1.03]
      transition-all
      "
                    >

                      Get Estimate

                      <ArrowRight className="ml-2 w-5 h-5" />

                    </Button>

                  </div>
                )}

                {/* Step 2 inline form */}
                {currentStep === 2 && (

                  <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: .5 }}
                    className="w-full"
                  >

                    <div className="grid lg:grid-cols-2 gap-10 items-center">

                      {/* LEFT */}

                      <div>

                        <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full mb-5">

                          <CheckCircle2 className="w-5 h-5" />

                          <span className="font-medium">

                            Almost Done

                          </span>

                        </div>

                        <h2 className="text-3xl font-bold text-gray-900 mb-4">

                          Get Your Free Rental Estimate

                        </h2>

                        <p className="text-gray-600 leading-7">

                          Share your contact details and our holiday home experts
                          will prepare a customized rental income report for your
                          property.

                        </p>

                        <div className="mt-8 space-y-4">

                          <div className="flex items-center gap-3">

                            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">

                              ✓

                            </div>

                            <span>

                              Free Consultation

                            </span>

                          </div>

                          <div className="flex items-center gap-3">

                            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">

                              ✓

                            </div>

                            <span>

                              Rental Income Projection

                            </span>

                          </div>

                          <div className="flex items-center gap-3">

                            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">

                              ✓

                            </div>

                            <span>

                              No Hidden Charges

                            </span>

                          </div>

                        </div>

                      </div>

                      {/* RIGHT */}

                      <div className="bg-white rounded-3xl shadow-xl border p-8">

                        <div className="grid gap-5">

                          <div>

                            <label className="text-sm font-medium text-gray-600 mb-2 block">

                              Full Name

                            </label>

                            <div className="relative">

                              {/* <User className="absolute left-4 top-4 w-5 h-5 text-gray-400" /> */}

                              <Input
                                className="
h-12
rounded-xl
border-gray-300
focus:border-primary
focus:ring-2
focus:ring-primary/20
"
                                placeholder="John Doe"
                                value={formData.name}
                                onChange={(e) => handleFormChange("name", e.target.value)}
                              />

                            </div>

                          </div>

                          <div>

                            <label className="text-sm font-medium text-gray-600 mb-2 block">

                              Mobile

                            </label>

                            <div className="relative">

                              {/* <Phone className="absolute left-4 top-4 w-5 h-5 text-gray-400" /> */}

                              <Input
                                className="
h-12
rounded-xl
border-gray-300
focus:border-primary
focus:ring-2
focus:ring-primary/20
"
                                placeholder="+971..."
                                value={formData.mobile}
                                onChange={(e) => handleFormChange("mobile", e.target.value)}
                              />

                            </div>

                          </div>

                          <div>

                            <label className="text-sm font-medium text-gray-600 mb-2 block">

                              Email

                            </label>

                            <div className="relative">

                              {/* <Mail className="absolute left-4 top-4 w-5 h-5 text-gray-400" /> */}

                              <Input
                                className="
h-12
rounded-xl
border-gray-300
focus:border-primary
focus:ring-2
focus:ring-primary/20
"
                                placeholder="john@email.com"
                                value={formData.email}
                                onChange={(e) => handleFormChange("email", e.target.value)}
                              />

                            </div>

                          </div>

                          <div>

                            <label className="text-sm font-medium text-gray-600 mb-2 block">

                              Property Name

                            </label>

                            <div className="relative">

                              {/* <Home className="absolute left-4 top-4 w-5 h-5 text-gray-400" /> */}

                              <Input
                                className="
h-12
rounded-xl
border-gray-300
focus:border-primary
focus:ring-2
focus:ring-primary/20
"
                                placeholder="Palm Villa"
                                value={formData.propertyName}
                                onChange={(e) => handleFormChange("propertyName", e.target.value)}
                              />

                            </div>

                          </div>

                          <div className="flex flex-col sm:flex-row gap-4 pt-4">

                            <Button
                              variant="outline"
                              className="
rounded-xl
h-12
font-semibold
shadow-lg
hover:shadow-xl
transition-all
duration-300
"
                              onClick={handleRestartFlow}
                            >

                              Back

                            </Button>

                            <Button
                              className="
rounded-xl
h-12
font-semibold
shadow-lg
hover:shadow-xl
transition-all
duration-300
"
                              onClick={handleSubmit}
                              disabled={loading}
                            >

                              {loading
                                ? "Submitting..."
                                : "Get My Estimate"}

                              <ArrowRight className="ml-2 w-5 h-5" />

                            </Button>

                          </div>

                        </div>

                      </div>

                    </div>

                  </motion.div>

                )}

                {currentStep === 3 && (

                  <motion.div
                    initial={{ opacity: 0, scale: .9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: .6 }}
                    className="w-full"
                  >

                    <div className="max-w-4xl mx-auto">

                      <div className="bg-white rounded-3xl shadow-2xl overflow-hidden">

                        <div className="grid lg:grid-cols-2">

                          {/* LEFT SIDE */}

                          <div className="bg-primary text-white p-10 flex flex-col justify-center">

                            <div className="w-20 h-20 rounded-full bg-white/20 flex items-center justify-center mb-8">

                              <CheckCircle2 className="w-12 h-12" />

                            </div>

                            <h2 className="text-4xl font-bold mb-4">

                              Thank You!

                            </h2>

                            <p className="text-lg leading-8 text-white/90">

                              Your request has been submitted successfully.

                              Our Holiday Home specialists will review your property
                              details and contact you shortly.

                            </p>

                            <div className="mt-10 space-y-4">

                              <div className="flex items-center gap-3">

                                ✓

                                <span>

                                  Property Review

                                </span>

                              </div>

                              <div className="flex items-center gap-3">

                                ✓

                                <span>

                                  Rental Income Analysis

                                </span>

                              </div>

                              <div className="flex items-center gap-3">

                                ✓

                                <span>

                                  Expert Consultation

                                </span>

                              </div>

                            </div>

                          </div>

                          {/* RIGHT SIDE */}

                          <div className="p-10">

                            <h3 className="text-2xl font-bold text-gray-900 mb-8">

                              What Happens Next?

                            </h3>

                            <div className="space-y-8">

                              <div className="flex gap-5">

                                <div className="w-12 h-12 rounded-full bg-primary text-white flex items-center justify-center font-bold">

                                  1

                                </div>

                                <div>

                                  <h4 className="font-semibold">

                                    Property Review

                                  </h4>

                                  <p className="text-gray-500">

                                    Our team reviews your property details.

                                  </p>

                                </div>

                              </div>

                              <div className="flex gap-5">

                                <div className="w-12 h-12 rounded-full bg-primary text-white flex items-center justify-center font-bold">

                                  2

                                </div>

                                <div>

                                  <h4 className="font-semibold">

                                    Rental Estimate

                                  </h4>

                                  <p className="text-gray-500">

                                    We'll prepare your rental income report.

                                  </p>

                                </div>

                              </div>

                              <div className="flex gap-5">

                                <div className="w-12 h-12 rounded-full bg-primary text-white flex items-center justify-center font-bold">

                                  3

                                </div>

                                <div>

                                  <h4 className="font-semibold">

                                    Expert Contact

                                  </h4>

                                  <p className="text-gray-500">

                                    One of our specialists will call you within 24 hours.

                                  </p>

                                </div>

                              </div>

                            </div>

                            <div className="mt-10">

                              <Button

                                className="w-full h-12 rounded-xl"

                                onClick={() => {
                                  setCurrentStep(1);

                                  setFormData({

                                    area: '',
                                    furnishStatus: '',
                                    selectedBeds: [],
                                    selectedBaths: [],
                                    propertyType: '',
                                    name: '',
                                    mobile: '',
                                    email: '',
                                    propertyName: ''

                                  });

                                }}

                              >

                                Submit Another Property

                              </Button>

                            </div>

                          </div>

                        </div>

                      </div>

                    </div>

                  </motion.div>

                )}
                {/* <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 mt-10">

                  <div className="text-center">

                    <h3 className="text-3xl font-bold text-white">

                      500+

                    </h3>

                    <p className="text-white/80">

                      Properties Managed

                    </p>

                  </div>

                  <div className="text-center">

                    <h3 className="text-3xl font-bold text-white">

                      AED 120M+

                    </h3>

                    <p className="text-white/80">

                      Rental Revenue

                    </p>

                  </div>

                  <div className="text-center">

                    <h3 className="text-3xl font-bold text-white">

                      4.9★

                    </h3>

                    <p className="text-white/80">

                      Client Rating

                    </p>

                  </div>

                  <div className="text-center">

                    <h3 className="text-3xl font-bold text-white">

                      24 hrs

                    </h3>

                    <p className="text-white/80">

                      Response Time

                    </p>

                  </div>

                </div> */}

                {/* Calculate Button */}

              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default LandlordHero;
